///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { add, parseISO, isPast, isEqual } from "date-fns";

import Modal from "../../Modal";
import { strategyDurationOptions } from "../../../helpers/types";
import messages from "../../../helpers/messages";
import AutoComplete from "../../form-controls/AutoComplete";
import CustomDatePicker from "../../form-controls/CustomDatePicker";
import getStrategyDuration from "../../../helpers/getStrategyDuration";
import { editBidStrategyDates } from "../../../api/bids";
import SystemErrorModal from "../../SystemErrorModal";
import Spinner from "../../icons/Spinner";

const initialStrategyFieldDetails = {
  strategyDuration: null,
  strategicStartDateUTC: null,
  strategicEndDateUTC: null
};

const initialEditBidStrategyDatesDetails = {
  loading: false,
  error: false
};

export const StrategySelectionModal = ({
  open,
  handleClose,
  showLaneImportModal,
  bidId
}) => {
  const {
    bidSummary: {
      summary: {
        strategicStartDateUTC,
        strategicEndDateUTC,
        contractEffectiveStartDateUTC
      }
    }
  } = useSelector(state => state);
  const [strategyFields, setStrategyFields] = useState(
    initialStrategyFieldDetails
  );
  const [editBidStrategyDatesDetails, setEditBidStrategyDatesDetails] =
    useState(initialEditBidStrategyDatesDetails);

  useEffect(() => {
    const startDate = isPast(parseISO(strategicStartDateUTC))
      ? new Date().toISOString()
      : strategicStartDateUTC;
    const endDate = isPast(parseISO(strategicEndDateUTC))
      ? new Date().toISOString()
      : strategicEndDateUTC;
    setStrategyFields({
      strategyDuration: getStrategyDuration(startDate, endDate),
      strategicStartDateUTC: startDate,
      strategicEndDateUTC: endDate
    });
  }, [strategicStartDateUTC, strategicEndDateUTC]);

  const handleStrategyDurationChange = option => {
    let strategicStartDate;

    if (strategyFields.strategicStartDateUTC) {
      strategicStartDate = parseISO(strategyFields.strategicStartDateUTC);
    } else {
      if (isPast(parseISO(contractEffectiveStartDateUTC))) {
        strategicStartDate = today;
      } else {
        strategicStartDate = parseISO(contractEffectiveStartDateUTC);
      }
    }

    setStrategyFields({
      strategyDuration: option,
      strategicStartDateUTC: strategicStartDate.toISOString(),
      strategicEndDateUTC: add(strategicStartDate, {
        months: option.months
      }).toISOString()
    });
  };

  const handleContinue = () => {
    const areDatesChanged =
      !isEqual(
        parseISO(strategicStartDateUTC).setHours(0, 0, 0, 0),
        parseISO(strategyFields.strategicStartDateUTC).setHours(0, 0, 0, 0)
      ) ||
      !isEqual(
        parseISO(strategicEndDateUTC).setHours(0, 0, 0, 0),
        parseISO(strategyFields.strategicEndDateUTC).setHours(0, 0, 0, 0)
      );

    if (areDatesChanged) {
      if (!editBidStrategyDatesDetails.loading) {
        setEditBidStrategyDatesDetails({ loading: true, error: false });
        editBidStrategyDates(
          bidId,
          strategyFields.strategicStartDateUTC,
          strategyFields.strategicEndDateUTC
        )
          .then(() => {
            setEditBidStrategyDatesDetails({ loading: false, error: false });
            showLaneImportModal();
          })
          .catch(() => {
            setEditBidStrategyDatesDetails({ loading: false, error: true });
          });
      }
    } else {
      showLaneImportModal();
    }
  };

  const today = new Date();
  const forecastMaxDate = add(today, {
    months: 18
  });

  return (
    <>
      <SystemErrorModal
        open={open && editBidStrategyDatesDetails.error}
        large
        closeModal={handleClose}
        handleExit={handleClose}
        handleTryAgain={handleContinue}
      />
      <Modal
        title={messages.laneImport.defaultMessage}
        open={open && !editBidStrategyDatesDetails.error}
        handleClose={handleClose}
        titleClass="modal__title_light uppercase"
        closeButtonClass="modal__close-button mr-24 mt-16 "
        bodyClass="border-top-grey p-24 strategy"
        large={true}
      >
        <p
          className="mb-24 font-size-16"
          data-testid="strategy-selection-modal"
        >
          {messages.strategySelectionModalBodyMessage.defaultMessage}
        </p>
        <div className="grid">
          <div
            className="grid__cell one-whole pos-relative"
            data-testid="strategy-duration-container"
          >
            <AutoComplete
              value={strategyFields.strategyDuration}
              label={messages.strategyDuration.defaultMessage}
              staticOptions={strategyDurationOptions}
              handleChange={handleStrategyDurationChange}
            />
          </div>
          <div className="grid__cell one-half pos-relative">
            <CustomDatePicker
              id="Strategic Effective Start Date"
              value={strategyFields.strategicStartDateUTC}
              label={messages.effectiveStartDate.defaultMessage}
              minDate={today}
              maxDate={
                strategyFields.strategicEndDateUTC
                  ? parseISO(strategyFields.strategicEndDateUTC)
                  : forecastMaxDate
              }
              onChange={date => {
                setStrategyFields({
                  ...strategyFields,
                  strategyDuration: getStrategyDuration(
                    date,
                    strategyFields.strategicEndDateUTC
                  ),
                  strategicStartDateUTC: date
                });
              }}
            />
          </div>
          <div className="grid__cell one-half pos-relative">
            <CustomDatePicker
              id="Strategic Effective End Date"
              value={strategyFields.strategicEndDateUTC}
              label={messages.effectiveEndDate.defaultMessage}
              minDate={
                strategyFields.strategicStartDateUTC
                  ? parseISO(strategyFields.strategicStartDateUTC)
                  : today
              }
              maxDate={forecastMaxDate}
              onChange={date => {
                setStrategyFields({
                  ...strategyFields,
                  strategyDuration: getStrategyDuration(
                    strategyFields.strategicStartDateUTC,
                    date
                  ),
                  strategicEndDateUTC: date
                });
              }}
            />
          </div>
        </div>
        <div className="grid fr mb-24 mt-16 ">
          <div className="grid__cell pr-8">
            <button
              data-testid="cancel-button"
              className="secondary-button one-whole cursor-pointer univers-67-bold-cond short-button"
              onClick={handleClose}
            >
              {messages.cancel.defaultMessage}
            </button>
          </div>
          <div className="grid__cell pl-8">
            <button
              data-testid="continue-button"
              className="primary-button one-whole cursor-pointer univers-67-bold-cond short-button"
              disabled={
                !strategyFields.strategicStartDateUTC ||
                !strategyFields.strategicEndDateUTC
              }
              onClick={handleContinue}
            >
              {editBidStrategyDatesDetails.loading ? (
                <Spinner
                  fill="#FFF"
                  className="spinner mt-4"
                  height="16px"
                  width="16px"
                />
              ) : (
                messages.continue.defaultMessage
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StrategySelectionModal;
